/**
 * Where is this record being drawn?
 *
 * The chat reuses the product's OWN tables and cell renderers, which is the
 * point — the rows look and behave exactly like the page's. But a few of those
 * cells are real `<Link>`s, and a link that is right on the Traces page is
 * wrong in the chat: it navigates away and the conversation is gone.
 *
 * Found live on 3 Sep, clicking the USER column of a runs table in an answer:
 * the row's own click handler opens the run in place, but DistinctIdLink calls
 * stopPropagation and navigates to the user's profile page instead — exactly
 * the "don't take me to another page" behaviour this surface is supposed to
 * have removed.
 *
 * Rather than thread a prop through every table into every cell, cells ask
 * where they are. Default is the page, so nothing outside the chat changes.
 */
import { createContext, useContext } from 'react';
import { openUserProfile } from 'features/users/openUserProfile';

const RecordsSurfaceContext = createContext('page');

export const RecordsSurfaceProvider = RecordsSurfaceContext.Provider;

/** True when these records are drawn inside a chat answer. */
export function useOpensInPlace() {
  return useContext(RecordsSurfaceContext) === 'chat';
}

/** Ask the chat to open an entity in place (drawer or full run view). */
export function openInChat(kind, id) {
  if (!id) return;
  if (kind === 'user') { openUserProfile(id); return; }
  window.dispatchEvent(new CustomEvent('trodo:chat:openEntity', { detail: { kind, id: String(id) } }));
}

/**
 * Open a product page BESIDE the conversation: the page becomes the main
 * view and the conversation moves to the side panel, as a dashboard Lucid
 * builds already does. "Open in editor" on an eval it created used to
 * navigate the chat away, and the answer with it.
 */
export function openBesideChat(path) {
  if (!path) return;
  window.dispatchEvent(new CustomEvent('trodo:openBeside', { detail: { path } }));
}

/** A plain click opens beside the chat; a modified click keeps the browser's own meaning. */
export function besideClick(path) {
  return (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openBesideChat(path);
  };
}
